/**
 * Parse yyyy-MM-dd from API (reporting TZ calendar day) as local midnight.
 * Local calendar components avoid UTC day-shift; midnight keeps DevExtreme day ticks
 * aligned with series points (noon previously sat halfway between midnight ticks).
 */
export function parseReportingChartDate(isoDate: string): Date {
  const parts = isoDate.split('-').map((p) => Number.parseInt(p, 10));
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return new Date(NaN);
  return new Date(parts[0], parts[1] - 1, parts[2], 0, 0, 0, 0);
}

/** Short local label for a reporting yyyy-MM-dd calendar day (e.g. "Aug 5"). */
export function formatReportingChartDayLabel(isoDate: string): string {
  const d = parseReportingChartDate(isoDate);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function formatActivityDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function formatSessionDuration(startedAt: string, endedAt: string | null, lastSeenAt: string): string {
  const start = new Date(startedAt).getTime();
  const end = new Date(endedAt ?? lastSeenAt).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return '—';
  const minutes = Math.round((end - start) / 60000);
  if (minutes < 1) return '<1 min';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  return rem === 0 ? `${hours}h` : `${hours}h ${rem}m`;
}

export function formatEndReason(value: string | null | undefined): string {
  if (!value) return 'Active';
  switch (value.toLowerCase()) {
    case 'logout':
      return 'Signed out';
    case 'expired':
      return 'Timed out';
    case 'superseded':
      return 'Replaced';
    default:
      return value;
  }
}

export function formatUserLabel(displayName: string | null, userName: string | null): string {
  return displayName?.trim() || userName?.trim() || 'Unknown user';
}

export function formatViewport(
  width: number | null | undefined,
  height: number | null | undefined
): string {
  if (width == null || height == null || width <= 0 || height <= 0) return '—';
  return `${width}×${height}`;
}

export function formatThemeLabel(family: string | null | undefined, mode: string | null | undefined): string {
  const f = family?.trim();
  const m = mode?.trim();
  if (!f && !m) return '—';
  if (f && m) return `${f} / ${m}`;
  return f ?? m ?? '—';
}
